import React, { useState, useEffect, useMemo, useRef } from 'react';
import useSearch from '../../hooks/useSearch';
import './Notification.css';
import { getAssetTypes, getNotificationSummary, getUserByCustomerID, assignUsers } from '../../Services/serviceURL';
import { useNavigate } from 'react-router-dom';
import Loader from '../../Components/common/loader/loader';
import SearchIcon from '../../Images/search-icon.svg';

interface Asset {
    assetId: string;
    assetType: string;
    assetName: string;
    notifyTo: string[];
    assignedUsers: string[];
    isActive: boolean;
}

interface AssetTypeResponse {
    typeName: string;
    // ...other fields if needed
}

interface Users {
    id: string,
    name: string,
    email: string
}

const Notification: React.FC = () => {
    const [assets, setAssets] = useState<Asset[]>([]);
    const [selected, setSelected] = useState<number[]>([]);
    const [assetTypes, setAssetTypes] = useState<string[]>([]);
    const [assetTypeFilter, setAssetTypeFilter] = useState('All');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [userSearchText, setUserSearchText] = useState('');
    const [isAssetDropdownOpen, setIsAssetDropdownOpen] = useState(false);
    const navigate = useNavigate();
    const dropdownRef = useRef<HTMLDivElement>(null);
    

    // Mock users data for the grid
    // ];
    const [availableUsers, setAvailableUsers] = useState<Users[]>([]);
    // Modal handlers
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => {
        setSelectedAssets([]);
        setSelectedUsers([]);
        setAvailableUsers([]);
        setUserSearchText('');
        setIsAssetDropdownOpen(false);
        setIsModalOpen(false);
    };

    const handleUserSelection = (userId: string) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleAssetSelection = (assetId: string) => {
        setSelectedAssets(prev => 
            prev.includes(assetId) 
                ? prev.filter(id => id !== assetId)
                : [...prev, assetId]
        );
    };

    const handleSelectAllAssets = () => {
        if (selectedAssets.length === filteredAssets.length) {
            // If all are selected, deselect all
            setSelectedAssets([]);
        } else {
            // Select all assets
            setSelectedAssets(filteredAssets.map(asset => asset.assetId));
        }
    };

    const handleSaveAssignment = async () => {
        try {
            // Handle multiple assets - save each asset separately
            for (const assetId of selectedAssets) {
                const assignedUsers = {
                    assetId: assetId,
                    isAcknowledgementRequired: assets.find(asset => asset.assetId === assetId)?.isActive,
                    users: selectedUsers
                };
                const response = await assignUsers(assignedUsers);
                if (response.StatusCode !== 200) {
                    console.error(`Failed to assign users to asset ${assetId}`);
                }
            }
            closeModal();
            getNotificationSummaryDetails();
        } catch (error) {
            console.error('Error assigning users:', error);
        }
    };

    // Fetch asset types from API on mount
    useEffect(() => {
        const fetchAssetTypes = async () => {
            try {
                const response = await getAssetTypes();

                const apiData = response.Data || response.data?.Data || [];
                if (Array.isArray(apiData)) {
                    setAssetTypes(apiData.map((t: AssetTypeResponse) => t.typeName));
                } else {
                    setAssetTypes([]);
                }
            } catch (err) {
                setAssetTypes([]);
            }
        };
        fetchAssetTypes();
        getNotificationSummaryDetails();
    }, []);

    const getNotificationSummaryDetails = async () => {
        const currentPlantID: string | null =
            localStorage.getItem("currentplantguid") ||
            localStorage.getItem("currentPlantId");

        if (!currentPlantID) {
            setAssets([]);
            return;
        }

        const summaryData = await getNotificationSummary(currentPlantID);
        const apiData = summaryData?.Data || [];
        const responseValue: Asset[] = apiData.map((notification: any) => ({
            assetId: notification.assetId,
            assetType: notification.assetType,
            assetName: notification.assetName,
            notifyTo: notification.users.map((user: any) => user.email),
            assignedUsers: notification.users.map((user: any) => user.id),
            isActive: notification.isAcknowledgementRequired
        }))

        setAssets(responseValue);
    }
    // useSearch for Asset type, Asset name, Notify to
    const { searchText, filteredData, handleSearchChange } = useSearch<Asset>(assets);

    // Filter by asset type after search
    const filteredAssets = (searchText.trim() === '' ? assets : filteredData).filter(asset =>
        assetTypeFilter === 'All' || asset.assetType === assetTypeFilter
    );

    const handleSelect = (idx: number) => {
        setSelected(sel =>
            sel.includes(idx) ? sel.filter(i => i !== idx) : [...sel, idx]
        );
    };

    const handleSelectAll = (checked: boolean) => {
        setSelected(checked ? filteredAssets.map((_, i) => i) : []);
    };

    // Toggle active/inactive for a row
    const handleToggleActive = async (idx: string) => {
        const findAsset = assets.find(asset => asset.assetId == idx);
        const updatedAsset = {
            assetId: findAsset?.assetId ? findAsset.assetId : '',
            isAcknowledgementRequired: !findAsset?.isActive,
            users: findAsset?.assignedUsers ? findAsset.assignedUsers : []
        }
        const response = await assignUsers(updatedAsset);
        if (response.StatusCode == 200) {
        }
        setAssets(prev =>
            prev.map((asset, i) =>
                asset.assetId === idx ? { ...asset, isActive: !asset.isActive } : asset
            )
        );
    };

    const loadUsersForSelectedAssets = async () => {
        if (selectedAssets.length > 0) {
            let customerID = localStorage.getItem("customerid");

            // Fallback: derive customerId from cached plant data if it wasn't persisted directly
            if (!customerID) {
                const storedPlantData = localStorage.getItem("currentPlantData");
                if (storedPlantData) {
                    try {
                        const parsedPlantData = JSON.parse(storedPlantData);
                        if (parsedPlantData?.customerId) {
                            customerID = parsedPlantData.customerId;
                            localStorage.setItem("customerid", customerID as string);
                        }
                    } catch (error) {
                        // Ignore parse errors, handled by the guard below
                    }
                }
            }

            if (!customerID) {
                setAvailableUsers([]);
                return;
            }

            const UserListResponse = await getUserByCustomerID(customerID);
            const UserListValues: Users[] = UserListResponse.Data;
            setAvailableUsers(UserListValues);
            
            // Get all assigned users from selected assets
            const allAssignedUsers: string[] = [];
            selectedAssets.forEach(assetId => {
                const users = assets.find(asset => asset.assetId === assetId)?.assignedUsers;
                if (users) {
                    allAssignedUsers.push(...users);
                }
            });
            setSelectedUsers(Array.from(new Set(allAssignedUsers))); // Remove duplicates
        } else {
            setAvailableUsers([]);
            setSelectedUsers([]);
        }
    };

    // Load users when assets are selected
    useEffect(() => {
        loadUsersForSelectedAssets();
    }, [selectedAssets]);

    // Handle clicking outside the dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsAssetDropdownOpen(false);
            }
        };

        if (isAssetDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isAssetDropdownOpen]);

    // Filter users based on search text (name and email)
    const filteredAvailableUsers = useMemo(() => {
        if (!userSearchText.trim()) {
            return availableUsers;
        }
        const searchLower = userSearchText.toLowerCase();
        return availableUsers.filter(user => 
            user.name.toLowerCase().includes(searchLower) || 
            user.email.toLowerCase().includes(searchLower)
        );
    }, [availableUsers, userSearchText]);
    const OnClickBack = () => {
        navigate('/create-plant-details')
    }

    // Tab navigation handler
    const handleTabNavigation = (tabNumber: number) => {
        const plantGuid = localStorage.getItem('currentplantguid') || localStorage.getItem('currentPlantId');
        
        switch (tabNumber) {
            case 1:
                // Navigate back to Create Plant page
                if (plantGuid) {
                    navigate(`/create-plant/${plantGuid}`);
                } else {
                    navigate('/create-plant');
                }
                break;
            case 2:
                // Navigate to CreatePlantDetails
                navigate('/create-plant-details');
                break;
            case 3:
                // Current page - do nothing
                break;
            default:
                break;
        }
    };
    return (
        <div className="notification-setup-container">

            <div className="create-plant-tabs">
                <div 
                    className={`tab completed ${assets.length > 0 ? 'clickable' : ''}`}
                    onClick={assets.length > 0 ? () => handleTabNavigation(1) : undefined}
                >
                    <span className="tab-number">1</span>
                    Enter Plant Details
                </div>
                <div 
                    className={`tab completed ${assets.length > 0 ? 'clickable' : ''}`}
                    onClick={assets.length > 0 ? () => handleTabNavigation(2) : undefined}
                >
                    <span className="tab-number">2</span>
                    Build Asset Hierarchy
                </div>
                <div className="tab active">
                    <span className="tab-number">3</span>
                    Notification Setup
                </div>
                <div className="tab-back-button">
                    <span className="Back-button ~download-button-plant" onClick={OnClickBack}>Back</span>
                </div>
            </div>
            <div className="notification-header-plant">

                <div className="tabsdiv">


                </div>

            </div>
            <div className="notification-toolbar">
                <div className="search-bar">
                    <img className="search-bar-icon" src={SearchIcon} alt="search" />
                    <input
                        type="text"
                        placeholder="Search By Asset Type, Name, Or Notify Email"
                        title="Search By Asset Type, Name, Or Notify Email"
                        value={searchText}
                        onChange={handleSearchChange}
                    />
                </div>
                <select
                    className="asset-type-select"
                    value={assetTypeFilter}
                    onChange={e => setAssetTypeFilter(e.target.value)}
                >
                    <option value="All">Asset type: All</option>
                    {assetTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>
                <button
                    className="assign-users-btn"
                    onClick={openModal}
                    disabled={filteredAssets.length === 0}
                >
                    👥 Assign user
                </button>
            </div>
            <div className="notification-table-container">
                <table className="notification-table">
                    <thead>
                        <tr>
                            <th>Asset Type</th>
                            <th>Asset Name</th>
                            <th>Notify To</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAssets.map((asset, idx) => (
                            <tr key={idx}>
                                <td>{asset.assetType}</td>
                                <td>{asset.assetName}</td>
                                <td>
                                    {asset.notifyTo.length > 1 ? (
                                        <span className="notify-multi" title={asset.notifyTo.join(', ')}>
                                            {asset.notifyTo.length} Emails
                                        </span>
                                    ) : (
                                        asset.notifyTo[0]
                                    )}
                                </td>
                                <td>
                                    <label className="switch-notification">
                                        <input
                                            type="checkbox"
                                            checked={asset.isActive}
                                            onChange={() => handleToggleActive(asset.assetId)}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                    <span className={asset.isActive ? 'toggle-label active' : 'toggle-label inactive'}>
                                        {asset.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        {filteredAssets.length === 0 && (
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>
                                    No Assets Found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Assign Users Modal */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{background: "#131f2e",maxWidth:'85%',maxHeight:'70%' }}>
                        <div className="modal-header">
                            <h3>Assign Users To Asset</h3>
                            <button className="modal-close" onClick={closeModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group-notify">
                                <label htmlFor="asset-select">Select Assets *</label>
                                <div className="multi-select-container" ref={dropdownRef}>
                                    <div className="multi-select-header" onClick={() => setIsAssetDropdownOpen(!isAssetDropdownOpen)}>
                                        <span>
                                            {selectedAssets.length === 0 
                                                ? 'Please Select Assets' 
                                                : `${selectedAssets.length} asset(s) selected`
                                            }
                                        </span>
                                        <span className={`dropdown-arrow-notify ${isAssetDropdownOpen ? 'open' : ''}`}>▼</span>
                                    </div>
                                    {isAssetDropdownOpen && (
                                        <div className="multi-select-options">
                                            <div className="multi-select-options-list">
                                                {/* Select All Option */}
                                                <div className="multi-select-option select-all-option">
                                                    <input
                                                        className='notify-check'
                                                        type="checkbox"
                                                        id="select-all-assets"
                                                        checked={selectedAssets.length === filteredAssets.length && filteredAssets.length > 0}
                                                        onChange={handleSelectAllAssets}
                                                    />
                                                    <label htmlFor="select-all-assets">
                                                        <strong>Select All ({filteredAssets.length})</strong>
                                                    </label>
                                                </div>
                                                {/* Individual Asset Options */}
                                                {filteredAssets.map((asset, index) => (
                                                    <div key={index} className="multi-select-option">
                                                        <input
                                                            type="checkbox"
                                                            id={`asset-${asset.assetId}`}
                                                            checked={selectedAssets.includes(asset.assetId)}
                                                            onChange={() => handleAssetSelection(asset.assetId)}
                                                        />
                                                        <label htmlFor={`asset-${asset.assetId}`}>
                                                            {asset.assetType} - {asset.assetName}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="multi-select-apply-footer">
                                                <button
                                                    type="button"
                                                    className="multi-select-apply-btn"
                                                    onClick={() => setIsAssetDropdownOpen(false)}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-group-notify">
                                <div className='notify-to-lbl'>
                                <label>Notify To *</label>
                                <div className="user-search-container">
                                    <img className="user-search-icon" src={SearchIcon} alt="search" />
                                    <input
                                        type="text"
                                        placeholder="Search by name or email"
                                        className="user-search-input"
                                        value={userSearchText}
                                        onChange={(e) => setUserSearchText(e.target.value)}
                                    />
                                </div>
                                </div>
                                {/* style={{maxHeight: 208, overflowY:"auto"}} */}
                                <div className="notification-users-grid" >
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Select</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                            </tr>
                                        </thead>
                                    </table>
                                    <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                                        <table>
                                            <tbody>
                                                {filteredAvailableUsers.length > 0 ? (
                                                    <>
                                                        {
                                                            filteredAvailableUsers.map((user) => (
                                                                <tr key={user.id} className="user-row">
                                                                    <td>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedUsers.includes(user.id)}
                                                                            onChange={() => handleUserSelection(user.id)}
                                                                        />
                                                                    </td>
                                                                    <td>{user.name}</td>
                                                                    <td>{user.email}</td>
                                                                </tr>
                                                            ))
                                                        }
                                                    </>

                                                ) : (
                                                    <tr style={{ height: "149px" }}><td colSpan={3} style={{ textAlign: "center" }}>No user found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="download-button-plant"
                                onClick={closeModal}

                            >
                                Close
                            </button>
                            <button
                                className="save-btn"
                                onClick={handleSaveAssignment}
                                disabled={selectedAssets.length === 0 || selectedUsers.length === 0}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Notification;
